import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users, Activity, FlaskConical, SignalHigh,
  ChevronDown, ChevronRight,
  User,
} from 'lucide-react';

const fmtMoney = (v) =>
  v != null ? `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

const fmtPct = (v) =>
  v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

// Helper to format currency/PnL colors
const formatPnL = (value) => {
  const num = Number(value) || 0;
  const color = num > 0 ? "text-emerald-600 dark:text-emerald-400" : num < 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground";
  const sign = num > 0 ? "+" : "";
  return <span className={`font-medium ${color}`}>{sign}{num.toLocaleString()}</span>;
};

// Helper to get initials for the avatar
const getInitials = (name, email) => {
  if (name) return name.substring(0, 2).toUpperCase();
  if (email) return email.substring(0, 2).toUpperCase();
  return <User className="w-4 h-4" />;
};

function StatCard({ label, value, sub, icon: Icon, isPositive, isMonetary = false }) {
  // Apply subtle background colors if it's a monetary/performance metric
  const bgTint = isMonetary
    ? isPositive
      ? 'bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/20'
      : 'bg-rose-500/5 dark:bg-rose-500/10 border-rose-500/20'
    : 'bg-card border-border';

  const iconTint = isMonetary
    ? isPositive ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/20 text-rose-600 dark:text-rose-400'
    : 'bg-primary/10 text-primary';

  return (
    <Card className={`transition-colors ${bgTint}`}>
      <CardContent className="p-4 flex items-start gap-3 relative">
        <div className={`absolute top-4 right-4 p-2 rounded-md shrink-0 ${iconTint}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1 pr-10">
          <p className="text-xs text-muted-foreground mb-1 font-semibold uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold leading-tight truncate text-foreground mb-1">{value}</p>
          {sub && (
            <p className={`text-xs font-medium ${
              isPositive === true ? 'text-emerald-600 dark:text-emerald-400'
                : isPositive === false ? 'text-rose-600 dark:text-rose-400'
                : 'text-muted-foreground'
            }`}>
              {sub}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4 flex items-start gap-3">
        <Skeleton className="w-8 h-8 rounded-md shrink-0" />
        <div className="flex-1">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-5 w-28" />
        </div>
      </CardContent>
    </Card>
  );
}

const Pct = ({ v }) => {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  return <span className={v >= 0 ? 'text-win' : 'text-loss'}>{fmtPct(v)}</span>;
};

// Column accessors for client-side sorting of the user table.
const SORTERS = {
  name: (u) => (u.name || u.email || '').toLowerCase(),
  status: (u) => u.status || '',
  last_activity: (u) => u.last_activity || '',
  paper_return: (u) => u.paper.latest_return_pct ?? -Infinity,
  paper_pnl: (u) => u.paper.total_pnl ?? -Infinity,
  live_return: (u) => u.live.latest_return_pct ?? -Infinity,
  live_pnl: (u) => u.live.total_pnl ?? -Infinity,
};

function SortHeader({ label, col, sort, setSort, align = 'left' }) {
  const active = sort.col === col;
  const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      className={`px-3 py-2 font-semibold cursor-pointer select-none hover:text-foreground whitespace-nowrap ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-foreground' : ''}`}
      onClick={() =>
        setSort((s) =>
          s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' }
        )
      }
    >
      {label}{arrow}
    </th>
  );
}

function BookSubTable({ title, icon: Icon, books, kind }) {
  // 1. Upgraded Empty State
  if (!books || books.length === 0) {
    return (
      <div className="mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
          <Icon className="w-4 h-4 text-muted-foreground" /> {title}
        </div>
        <div className="flex items-center justify-center p-6 border border-dashed border-border/60 rounded-lg bg-background/30 text-xs text-muted-foreground">
          No {kind} books available.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Title Header */}
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
        <Icon className="w-4 h-4 text-muted-foreground" /> {title}
      </div>
      
      {/* 2. Card Enclosure for the Table */}
      <div className="rounded-lg border border-border/60 overflow-hidden bg-background/50 shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            
            {/* 3. Refined Typography for Headers */}
            <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Book</th>
                <th className="px-4 py-2.5 font-semibold text-right">Portfolio</th>
                <th className="px-4 py-2.5 font-semibold text-right">Return</th>
                <th className="px-4 py-2.5 font-semibold text-right">P&L</th>
                <th className="px-4 py-2.5 font-semibold text-right">Trades</th>
                <th className="px-4 py-2.5 font-semibold text-right">Win %</th>
                <th className="px-4 py-2.5 font-semibold text-right">Open</th>
                <th className="px-4 py-2.5 font-semibold text-right">As of</th>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-border/40">
              {books.map((b) => (
                <tr key={b.id} className="hover:bg-surface-hover/40 transition-colors">
                  
                  {/* Book Name & Badges */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{b.name}</span>
                      
                      {/* 4. Pill Badges for Status */}
                      <div className="flex items-center gap-1.5">
                        {b.status !== 'active' && (
                          <span className="px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 ring-1 ring-amber-500/30 text-[9px] font-bold uppercase tracking-wide">
                            {b.status}
                          </span>
                        )}
                        {kind === 'live' && b.is_dry_run && (
                          <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground ring-1 ring-border text-[9px] font-bold uppercase tracking-wide">
                            Dry-Run
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Data Columns */}
                  <td className="px-4 py-2.5 text-right font-medium">{fmtMoney(b.portfolio_value)}</td>
                  
                  <td className="px-4 py-2.5 text-right">
                    <Pct v={b.cumulative_return_pct} />
                  </td>
                  
                  {/* 5. Colored PnL */}
                  <td className="px-4 py-2.5 text-right">
                    {formatPnL(b.summary?.total_pnl)}
                  </td>
                  
                  <td className="px-4 py-2.5 text-right text-muted-foreground">
                    {b.summary?.total_trades ?? 0}
                  </td>
                  
                  <td className="px-4 py-2.5 text-right font-medium">
                    {b.summary?.win_rate_pct ?? 0}%
                  </td>
                  
                  <td className="px-4 py-2.5 text-right text-muted-foreground">
                    {b.open_positions ?? 0}
                  </td>
                  
                  <td className="px-4 py-2.5 text-right text-[11px] text-muted-foreground whitespace-nowrap">
                    {fmtDate(b.as_of)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UserRow({ u, expanded, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!expanded || detail || loadingDetail) return;
    setLoadingDetail(true);
    api.get(`/admin/analytics/users/${u.id}`)
      .then((res) => setDetail(res.data))
      .catch(() => setError('Failed to load books'))
      .finally(() => setLoadingDetail(false));
  }, [expanded, detail, loadingDetail, u.id]);

  return (
    <>
      <tr 
        className={`border-b border-border/50 hover:bg-surface-hover/80 transition-colors cursor-pointer ${expanded ? 'bg-surface-hover/40' : ''}`} 
        onClick={onToggle}
      >
        {/* 1. User Info Column with Avatar */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="shrink-0 mt-0.5 text-muted-foreground transition-transform">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </div>
            
            {/* Avatar Circle */}
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">
              {getInitials(u.name, u.email)}
            </div>

            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">
                {u.name || u.email.split('@')[0]}
              </div>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
            </div>
          </div>
        </td>

        {/* 2. Last Activity */}
        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap font-medium">
          {fmtDate(u.last_activity)}
        </td>

        {/* --- LIVE TRADING GROUP --- */}
        <td className="px-4 py-3 text-right text-sm border-l border-border/40">
          <Pct v={u.live.latest_return_pct} />
        </td>
        <td className="px-4 py-3 text-right text-sm">
          {formatPnL(u.live.total_pnl)}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex flex-col items-end">
            <span className="text-xs font-semibold text-foreground">{u.live.win_rate_pct}%</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{u.live.total_trades} Trades</span>
          </div>
        </td>

        {/* --- PAPER TRADING GROUP --- 
            Adding a subtle right border or background can help group these */}
        <td className="px-4 py-3 text-right text-sm border-l border-border/40">
          <Pct v={u.paper.latest_return_pct} />
        </td>
        <td className="px-4 py-3 text-right text-sm">
          {formatPnL(u.paper.total_pnl)}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex flex-col items-end">
            <span className="text-xs font-semibold text-foreground">{u.paper.win_rate_pct}%</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{u.paper.total_trades} Trades</span>
          </div>
        </td>
      </tr>

      {/* EXPANDED STATE */}
      {expanded && (
        <tr>
          <td colSpan={8} className="p-0 border-b border-border/50">
            {/* The inset shadow and left border visually connect it to the parent row */}
            <div className="px-6 py-4 bg-muted/10 shadow-inner border-l-4 border-primary">
              {loadingDetail ? (
                <div className="space-y-3">
                   <Skeleton className="h-12 w-full rounded-md" />
                   <Skeleton className="h-12 w-full rounded-md" />
                </div>
              ) : error ? (
                <div className="p-4 rounded-md bg-destructive/10 text-destructive text-sm font-medium">
                  {error}
                </div>
              ) : detail ? (
                <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-top-2 duration-200">
                  <BookSubTable title="Live Books" icon={SignalHigh} books={detail.live_books} kind="live" />
                  <BookSubTable title="Paper Books" icon={FlaskConical} books={detail.paper_books} kind="paper" />
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [sort, setSort] = useState({ col: 'last_activity', dir: 'desc' });

  useEffect(() => {
    api.get('/admin/analytics/overview')
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const sortedUsers = useMemo(() => {
    if (!data?.users) return [];
    const accessor = SORTERS[sort.col] || SORTERS.name;
    const arr = [...data.users].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data, sort]);

  const p = data?.platform;

  return (
    <div className="space-y-6">
        
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">Platform Analytics</h1>
          <p className="text-sm text-muted-foreground">Monitor aggregate performance and individual user metrics.</p>
        </div>

        {error && <div className="p-4 bg-destructive/10 text-destructive text-sm rounded-md">{error}</div>}

        {/* 1. Enhanced KPI Dashboard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {loading || !p ? (
            [1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              <StatCard
                label="Total Users"
                value={p.total_users}
                sub={`${p.users_by_status.approved} approved · ${p.users_by_status.pending} pending`}
                icon={Users}
              />
              <StatCard
                label="Live Books"
                value={p.live_book_count}
                sub={`${p.live_real_book_count} real · ${p.live_book_count - p.live_real_book_count} dry-run`}
                icon={SignalHigh}
              />
              <StatCard
                label="Live P&L (Realised)"
                value={fmtMoney(p.live_total_pnl)}
                sub="Across all active users"
                icon={Activity}
                isPositive={p.live_total_pnl >= 0}
                isMonetary={true}
              />
              <StatCard
                label="Paper P&L (Realised)"
                value={fmtMoney(p.paper_total_pnl)}
                sub={`${p.paper_book_count} active paper books`}
                icon={FlaskConical}
                isPositive={p.paper_total_pnl >= 0}
                isMonetary={true}
              />
            </>
          )}
        </div>

        {/* 2. Grouped Table Layout */}
        <Card className="overflow-hidden border-border/60 shadow-xs">
          <div className="px-5 py-4 border-b border-border/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-surface-card">
            <div>
              <h2 className="text-base font-semibold text-foreground">User Performance</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Metrics calculated from each book's latest equity snapshot.
              </p>
            </div>
          </div>
          
          <div className="bg-card">
            {loading ? (
              <div className="p-5 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : sortedUsers.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center border-dashed border border-border/50 mx-4 my-4 rounded-lg">
                <Users className="w-8 h-8 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-foreground">No users found</p>
                <p className="text-xs text-muted-foreground">User data will appear here once approved.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  {/* TWO-TIER STICKY HEADER */}
                  <thead className="bg-muted/40 text-xs text-muted-foreground border-b border-border/60 sticky top-0 z-10 backdrop-blur-md">
                    {/* Top Tier: Category Grouping */}
                    <tr>
                      <th colSpan="2" className="px-4 py-2 border-b border-border/40 font-semibold tracking-wider uppercase text-[10px]">
                        User Details
                      </th>
                      <th colSpan="3" className="px-4 py-2 border-b border-l border-border/40 text-center font-bold tracking-wider uppercase text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20">
                        Live Trading
                      </th>
                      <th colSpan="3" className="px-4 py-2 border-b border-l border-border/40 text-center font-bold tracking-wider uppercase text-[10px]">
                        Paper Trading
                      </th>
                    </tr>
                    {/* Bottom Tier: Sortable Columns */}
                    <tr className="border-b border-border/50">
                      <SortHeader label="User" col="name" sort={sort} setSort={setSort} />
                      <SortHeader label="Last Activity" col="last_activity" sort={sort} setSort={setSort} />
                      
                      {/* Live Trading Columns */}
                      <SortHeader label="Return" col="live_return" sort={sort} setSort={setSort} align="right" className="border-l border-border/40 bg-blue-50/30 dark:bg-blue-900/10" />
                      <SortHeader label="P&L" col="live_pnl" sort={sort} setSort={setSort} align="right" className="bg-blue-50/30 dark:bg-blue-900/10" />
                      <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap bg-blue-50/30 dark:bg-blue-900/10">Win Rate / Trades</th>
                      
                      {/* Paper Trading Columns */}
                      <SortHeader label="Return" col="paper_return" sort={sort} setSort={setSort} align="right" className="border-l border-border/40" />
                      <SortHeader label="P&L" col="paper_pnl" sort={sort} setSort={setSort} align="right" />
                      <th className="px-4 py-2.5 text-right font-semibold whitespace-nowrap pr-6">Win Rate / Trades</th>  
                    </tr>
                  </thead>
                  
                  <tbody className="divide-y divide-border/40">
                    {sortedUsers.map((u) => (
                      <React.Fragment key={u.id}>
                        {u.status === 'approved' && (
                          <UserRow
                            u={u}
                            expanded={expandedId === u.id}
                            onToggle={() => setExpandedId((id) => (id === u.id ? null : u.id))}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
    </div>
  );
}
